import { supabase } from "@/integrations/supabase/client";
import type { ShiftCode } from "./shiftCodeService";

export interface MonthlyRosterRow {
  profile_id: string;
  employee_name: string;
  role: string;
  days: Record<string, {
    shift_code: string;
    shift_id: string | null;
    note: string | null;
    is_published?: boolean; // For split shifts that create multiple shift records
  }>;
}

export interface ScheduledShiftInput {
  profile_id: string;
  date: string; // YYYY-MM-DD
  shift_code: string;
  note?: string | null;
  split_segments?: Array<{
    label?: string;
    start_time: string;
    end_time: string;
  }> | null;
}

export interface RosterResponse {
  success: boolean;
  created_count?: number;
  published_count?: number;
  skipped_count?: number;
  deleted_count?: number;
  error_message?: string | null;
}

export interface IndividualShiftDeletion {
  profile_id: string;
  date: string; // YYYY-MM-DD
  year: number;
  month: number;
}

/**
 * Fetch monthly roster for a given year/month
 */
export const fetchMonthlyRoster = async (
  year: number,
  month: number
): Promise<MonthlyRosterRow[]> => {
  const { data, error } = await supabase.rpc('list_monthly_roster', {
    p_year: year,
    p_month: month
  });

  if (error) {
    console.error('Error fetching monthly roster:', error);
    throw new Error(error.message || 'Failed to fetch monthly roster');
  }

  // Type assertion for RPC response
  return (data as unknown as MonthlyRosterRow[]) || [];
};

/**
 * Bulk save scheduled shifts
 */
export const saveScheduledShifts = async (
  shifts: ScheduledShiftInput[]
): Promise<RosterResponse> => {
  const { data, error } = await supabase.rpc('bulk_create_scheduled_shifts', {
    p_scheduled_shifts: shifts as unknown as any // Type assertion for JSONB parameter
  });

  if (error) {
    console.error('Error saving scheduled shifts:', error);
    throw new Error(error.message || 'Failed to save scheduled shifts');
  }

  // RPC returns TABLE, so data is an array with one row
  const result = data?.[0] as RosterResponse;
  
  if (!result?.success) {
    throw new Error(result?.error_message || 'Failed to save scheduled shifts');
  }

  return result;
};

/**
 * Publish roster to weekly schedule (converts scheduled_shifts → shifts)
 */
export const publishMonthlyRoster = async (
  year: number,
  month: number
): Promise<RosterResponse> => {
  const { data, error } = await supabase.rpc('publish_monthly_roster', {
    p_year: year,
    p_month: month
  });

  if (error) {
    console.error('Error publishing roster:', error);
    throw new Error(error.message || 'Failed to publish roster');
  }

  const result = data?.[0] as RosterResponse;
  
  if (!result?.success) {
    throw new Error(result?.error_message || 'Failed to publish roster');
  }

  return result;
};

/**
 * Delete scheduled shifts for a given year/month (with optional profile filter)
 */
export const deleteScheduledShifts = async (
  year: number,
  month: number,
  profileIds?: string[]
): Promise<RosterResponse> => {
  const { data, error } = await supabase.rpc('delete_scheduled_shifts', {
    p_year: year,
    p_month: month,
    p_profile_ids: profileIds || null
  });

  if (error) {
    console.error('Error deleting scheduled shifts:', error);
    throw new Error(error.message || 'Failed to delete scheduled shifts');
  }

  const result = data?.[0] as RosterResponse;
  
  if (!result?.success) {
    throw new Error(result?.error_message || 'Failed to delete scheduled shifts');
  }

  return result;
};

/**
 * Fetch shift codes for the current company
 */
export const fetchShiftCodes = async (): Promise<ShiftCode[]> => {
  // Ensure user context is set before making queries that involve RLS policies
  await supabase.rpc('ensure_user_context');
  
  const { data, error } = await supabase
    .from('shift_codes')
    .select('*')
    .eq('is_active', true)
    .order('code');

  if (error) {
    console.error('Error fetching shift codes:', error);
    throw new Error(error.message || 'Failed to fetch shift codes');
  }

  return (data || []) as unknown as ShiftCode[];
};

/**
 * Delete individual scheduled shifts by specific profile and date
 */
export const deleteIndividualShifts = async (
  deletions: IndividualShiftDeletion[]
): Promise<RosterResponse> => {
  const { data, error } = await supabase.rpc('delete_individual_scheduled_shifts', {
    p_deletions: deletions as unknown as any // Type assertion for JSONB parameter
  });

  if (error) {
    console.error('Error deleting individual shifts:', error);
    throw new Error(error.message || 'Failed to delete individual shifts');
  }

  const result = data?.[0] as RosterResponse;
  
  if (!result?.success) {
    throw new Error(result?.error_message || 'Failed to delete individual shifts');
  }

  return result;
};

/**
 * Seed default shift codes for a company
 */
export const seedDefaultShiftCodes = async (companyId: string): Promise<number> => {
  const { data, error } = await supabase.rpc('seed_default_shift_codes', {
    p_company_id: companyId
  });

  if (error) {
    console.error('Error seeding shift codes:', error);
    throw new Error(error.message || 'Failed to seed shift codes');
  }

  return data || 0;
};
